import {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useState,
} from 'react';

import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label as ComboboxLabel,
} from '@headlessui/react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconPlus, IconZoomOutline } from '@iwoca/icons';
import { Button, Input, Label, Spinner, Tooltip } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';
import { useFormContext } from 'react-hook-form';

import styles from './CompanyInputSearchDE.module.css';
import { useGetCompanySearch } from '../../../../../api/companySearch/useGetCompanySearch';
import { fetchGetLegalForm } from '../../../../../api/lendingApi/apiQueries';
import { useGetSelectStateQuery } from '../../../../../api/lendingApi/lendingApiQueries';
import { useIsInDECompanySearchUITest } from '../../../../../hooks/useIsInDECompanySearchUITest';
import {
  TGetCompanySearchResponseDE,
  TGetStateResponseDE,
} from '../../../../../lapiOverrideTypesDE';
import logger from '../../../../../utils/logger';
import ButtonLink from '../../../../ButtonLink/ButtonLink';
import { normaliseStepDataDE } from '../../../../helpers/stateNormalisation';
import { BUSINESS_STEP_ID } from '../../../../helpers/steps';
import { TCompanyDetailsDataDE } from '../../CompanyDetailsDE';

type TCompanySearchCompany = TGetCompanySearchResponseDE['data'][number];

type TProps = {
  setManualInputTrue: () => void;
  searchedCompaniesCount: number;
  setSearchedCompaniesCount: Dispatch<SetStateAction<number>>;
  reportCompanyNameCompletion: (isComplete: boolean) => void;
};

const CompanyInputSearchDE = ({
  setManualInputTrue,
  searchedCompaniesCount,
  setSearchedCompaniesCount,
  reportCompanyNameCompletion,
}: TProps) => {
  const { normalisedState } = useGetSelectStateQuery(
    (data: TGetStateResponseDE) => normaliseStepDataDE(data)[BUSINESS_STEP_ID],
  );

  const { t } = useLingui();

  const {
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext<TCompanyDetailsDataDE>();

  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');

  const [showDefaultCompany, setShowDefaultCompany] = useState(
    Boolean(getValues('companyName') && getValues('street')),
  );

  const [selectedCompany, setSelectedCompany] =
    useState<TCompanySearchCompany>();

  const [companyOptions, setCompanyOptions] = useState<TCompanySearchCompany[]>(
    [],
  );

  const { isIncludedInCompanySearchUITest } = useIsInDECompanySearchUITest();

  const {
    data: companySearchData,
    isFetching,
    isError,
  } = useGetCompanySearch({
    searchQuery: debouncedQuery,
    enabled: !!debouncedQuery && debouncedQuery.length >= 3,
  });

  useEffect(() => {
    const setQueryTimer = setTimeout(() => {
      if (searchQuery && searchQuery.length >= 3) {
        setSearchedCompaniesCount((prevCount) => prevCount + 1);
        setDebouncedQuery(searchQuery);
      } else {
        setDebouncedQuery('');
      }
    }, 300);

    return () => clearTimeout(setQueryTimer);
  }, [searchQuery, setSearchedCompaniesCount]);

  useEffect(() => {
    if (debouncedQuery.length < 3) {
      setCompanyOptions([]);
      setSelectedCompany(undefined);
      return;
    }

    if (companySearchData) {
      tracking.ampli.companySearchResponseShown({
        query_length: debouncedQuery.length,
        response_length: companySearchData.data.length,
      });

      setCompanyOptions(companySearchData.data);
    }

    if (isError) {
      setCompanyOptions([]);
    }
  }, [companySearchData, isError, debouncedQuery]);

  useEffect(() => {
    if (
      // normalised state will always exist because this component is not
      // rendered unless state exists, but left for defensive reasons.
      normalisedState &&
      normalisedState.companyName &&
      !normalisedState.crefoId
    ) {
      setShowDefaultCompany(false);
      setValue('companyName', '');
    }
  }, [normalisedState]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isFetching) {
      e.preventDefault();
    }
  };

  const handleSetCompanyType = async (crefoId: string) => {
    try {
      const response = await fetchGetLegalForm(crefoId);

      if (!response?.data?.legal_form) {
        throw new Error('Invalid response for legal form');
      }

      setValue('legalForm', response?.data?.legal_form, {
        shouldValidate: true,
      });

      if (response.data.foundation_date) {
        setValue('crefoFoundationDate', response.data.foundation_date);
      }
    } catch (error) {
      if (error instanceof Error) {
        logger.error('Failed to set company type', {
          error: error.message,
        });
      }
    }
  };

  const handleChange = async (company: TCompanySearchCompany) => {
    if (!company) {
      setSelectedCompany(undefined);
      return;
    }

    tracking.ampli.companyNameSearched();
    setSelectedCompany(company);

    setValue('companyName', company.name, {
      shouldValidate: true,
    });
    reportCompanyNameCompletion(true);

    setValue('crefoId', company.crefo_id, {
      shouldValidate: true,
    });

    setValue('buildingNumber', company.house_number, {
      shouldValidate: true,
    });

    setValue('street', company.street, {
      shouldValidate: true,
    });

    setValue('postcode', company.postcode, {
      shouldValidate: true,
    });

    setValue('town', company.city, {
      shouldValidate: true,
    });

    await handleSetCompanyType(company.crefo_id);
  };

  const resetSelectedCompany = () => {
    setSearchQuery(undefined);
    setSelectedCompany(undefined);
    setShowDefaultCompany(false);

    setValue('companyName', '');
    setValue('crefoId', '');
    setValue('buildingNumber', '');
    setValue('street', '');
    setValue('postcode', '');
    setValue('town', '');
    setValue('legalForm', undefined);
    setValue('crefoFoundationDate', undefined);
    reportCompanyNameCompletion(false);
  };

  const buildDefaultValue = () =>
    getValues('companyName') && getValues('town')
      ? `${getValues('companyName')} | ${getValues('town')}`
      : '';

  const handleEnterManuallyClicked = (
    position: 'Complete' | 'Incomplete' | 'Not found',
  ) => {
    tracking.ampli.enterCompanyManuallyClicked({
      searched_companies_count: searchedCompaniesCount,
      version_of_button: position,
    });

    if (searchQuery && !selectedCompany && !showDefaultCompany) {
      setValue('companyName', searchQuery);
    }

    setManualInputTrue();
  };

  const renderOptions = () => {
    if (isFetching || !debouncedQuery) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner dataTestId="loading-spinner" />
        </div>
      );
    }

    return (
      <>
        {companyOptions.length > 0 ? (
          companyOptions.map((company, index) => (
            <ComboboxOption
              className={({ focus }) =>
                clsx(
                  styles.option,
                  {
                    [styles.focus]: focus,
                  },
                  'fs-mask',
                  'dd-privacy-mask',
                )
              }
              key={index}
              value={company}
            >
              <span className={styles.optionName}>{company.name}</span>
              <span className={styles.optionCity}>{company.city}</span>
            </ComboboxOption>
          ))
        ) : (
          <div className={styles.enterManuallyText}>
            <p>
              <Trans>
                Still have not found it? Try searching for the managing director
                name.
              </Trans>
            </p>

            <p>
              <Trans>Alternatively</Trans>,{' '}
              <ButtonLink
                className={styles.enterManually}
                type="button"
                onClick={() => handleEnterManuallyClicked('Not found')}
              >
                <Trans>Enter the details manually.</Trans>
              </ButtonLink>
            </p>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.companySearchContainer}>
      <Combobox onChange={handleChange}>
        <div className={styles.companiesSearch}>
          {selectedCompany || showDefaultCompany ? (
            <>
              <div className={styles.companyNameHeader}>
                <Label
                  htmlFor="companyDisplayName"
                  className={clsx({
                    [styles.labelWithTooltip]: isIncludedInCompanySearchUITest,
                  })}
                >
                  {isIncludedInCompanySearchUITest ? (
                    <Trans>Business name search</Trans>
                  ) : (
                    <Trans>Limited company</Trans>
                  )}

                  {isIncludedInCompanySearchUITest && (
                    <Tooltip
                      dataTestId="company-search-tooltip"
                      content={t`Search for your company name or the name of the managing director.`}
                    />
                  )}
                </Label>

                {!isIncludedInCompanySearchUITest && (
                  <ButtonLink
                    className={styles.enterManually}
                    type="button"
                    onClick={() => handleEnterManuallyClicked('Complete')}
                  >
                    <Trans>Company not found?</Trans>
                  </ButtonLink>
                )}
              </div>

              <Input
                id="companyDisplayName"
                className={`${styles.companyInput} fs-mask`}
                value={buildDefaultValue()}
                disabled
                error={
                  (errors?.companyName?.message &&
                    errors.companyName.message) ||
                  (errors?.street?.message && errors.street.message)
                }
                iconRight={
                  <Button
                    className={styles.clearButton}
                    type="reset"
                    variant="tertiary"
                    size="small"
                    data-testid="clear-button-company"
                    onClick={resetSelectedCompany}
                  >
                    <IconPlus
                      className={styles.clearButtonIcon}
                      height="24"
                      width="24"
                    />
                  </Button>
                }
              />
            </>
          ) : (
            <>
              <div className={styles.companyNameHeader}>
                <ComboboxLabel
                  htmlFor="companyDisplayName"
                  className={clsx({
                    [styles.labelWithTooltip]: isIncludedInCompanySearchUITest,
                  })}
                >
                  {isIncludedInCompanySearchUITest ? (
                    <Trans>Business name search</Trans>
                  ) : (
                    <Trans>Limited company</Trans>
                  )}

                  {isIncludedInCompanySearchUITest && (
                    <Tooltip
                      dataTestId="company-search-tooltip"
                      content={t`Search for your company name or the name of the managing director.`}
                    />
                  )}
                </ComboboxLabel>

                {!isIncludedInCompanySearchUITest && (
                  <ButtonLink
                    className={styles.enterManually}
                    type="button"
                    onClick={() => handleEnterManuallyClicked('Incomplete')}
                  >
                    <Trans>Company not found?</Trans>
                  </ButtonLink>
                )}
              </div>

              <ComboboxInput
                name="companyName"
                data-defer-completion="true"
                as={Input}
                iconRight={!selectedCompany ? <IconZoomOutline /> : undefined}
                placeholder={
                  isIncludedInCompanySearchUITest
                    ? t`Example company name or director name`
                    : t`Search a company name or number`
                }
                className={`${styles.companyInput} fs-mask`}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  tracking.ampli.companySearchInputChanged({
                    query_length: event.target.value.length,
                  });
                }}
                onKeyDown={handleKeyDown}
                error={
                  (errors?.companyName?.message &&
                    errors.companyName.message) ||
                  (errors?.street?.message && errors.street.message)
                }
              />
            </>
          )}
          {searchQuery && searchQuery.length >= 3 && (
            <ComboboxOptions className={styles.options} modal={false}>
              {renderOptions()}
            </ComboboxOptions>
          )}
        </div>
      </Combobox>

      {isIncludedInCompanySearchUITest && (
        <ButtonLink
          className={clsx(styles.enterManually, styles.enterManuallyWithMargin)}
          type="button"
          onClick={() =>
            handleEnterManuallyClicked(
              selectedCompany || showDefaultCompany ? 'Complete' : 'Incomplete',
            )
          }
        >
          <Trans>Business not found?</Trans>
        </ButtonLink>
      )}
    </div>
  );
};

export default CompanyInputSearchDE;
